import React, { useRef, useEffect, useContext } from 'react';
import { EditModeContext } from '../context/EditModeContext';
import clsx from 'clsx';

const EditableCell = ({
  row,
  column,
  value: initialValue,
  onSave,
  className
}) => {
  const {
    isEditMode,
    activeCell,
    selectedRange,
    startCellEdit,
    endCellEdit,
    updateCell,
    getEditedValue,
    handleKeyDown
  } = useContext(EditModeContext);

  const inputRef = useRef(null);
  
  const isInSelectedRange = selectedRange &&
    column.field === selectedRange.columnId &&
    (selectedRange.selectedIds
      ? selectedRange.selectedIds.includes(row.id.toString())
      : Number(row.id) >= Math.min(selectedRange.startRow, selectedRange.endRow) &&
        Number(row.id) <= Math.max(selectedRange.startRow, selectedRange.endRow));

  const isEditing = (activeCell?.rowId === row.id && activeCell?.columnId === column.field) || 
                   (isInSelectedRange && selectedRange?.columnId === column.field);
  const shouldShowInput = isEditing;
  
  const currentValue = getEditedValue(row.id, column.field) ?? initialValue;
  const displayValue = currentValue === null || currentValue === undefined || currentValue === 'null' ? '' : currentValue;

  useEffect(() => {
    // Only focus and select the active cell's input
    if (isEditing && inputRef.current) {
      requestAnimationFrame(() => {
        inputRef.current.focus();
        inputRef.current.select();
      });
    }
  }, [isEditing]);

  const handleClick = (e) => {
    if (!isEditMode) return;
    
    e.preventDefault(); // Prevent text selection
    e.stopPropagation(); // Prevent row click

    if (e.shiftKey) {
      // Handle range selection
      startCellEdit(row.id, column.field, true);
    } else {
      // Any regular click should clear range and select just this cell
      startCellEdit(row.id, column.field, false);
    }
  };

  // Prevent text selection on shift-click
  const handleMouseDown = (e) => {
    if (isEditMode && e.shiftKey) {
      e.preventDefault();
    }
  };

  const handleBlur = () => {
    if (isEditing) {
      endCellEdit();
    }
  };

  const handleChange = (e) => {
    updateCell(row.id, column.field, e.target.value);
  };

  if (!isEditMode) {
    return (
      <div className={clsx(
        'px-4 h-full flex items-center w-full',
        'whitespace-nowrap overflow-hidden text-ellipsis',
        className
      )}>
        {displayValue}
      </div>
    );
  }

  // Wrap all non-input content in a clickable container
  const cellContent = shouldShowInput ? (
    <div className="h-full flex items-center">
      <input
        ref={inputRef}
        value={currentValue === null || currentValue === undefined || currentValue === 'null' ? '' : currentValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className={clsx(
          'w-full h-full px-4',
          'focus:outline-none',
          'bg-white',
          className
        )}
      />
    </div>
  ) : (
      <div className={clsx(
        'w-full h-full px-4',
        'whitespace-nowrap overflow-hidden text-ellipsis',
        'flex items-center min-h-[24px]',
        isInSelectedRange && 'bg-indigo-100',
        getEditedValue(row.id, column.field) && 'font-medium text-blue-600',
        !displayValue && 'bg-gray-50/50',
        className
      )}>
      {displayValue}
    </div>
  );

  return (
    <div
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      className={clsx(
        'w-full h-full',
        'cursor-cell',
        'hover:bg-indigo-50/50'
      )}
    >
      {cellContent}
    </div>
  );
};

export default EditableCell;
